import { ClickEvent, Layout, SlideoutDecorator, TableRow } from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { ShipmentStatusSlideout } from "./ShipmentStatusSlideout";
import { AutogenLayoutMovementDataFusionSummary } from "./autogen/AutogenLayoutMovementDataFusionSummary";

export class MovementDataFusionSummary extends AutogenLayoutMovementDataFusionSummary {
    private _movementId: string;
    private _orderId: string;
    private _customerId: string;
    private _displayingData: boolean = true;
    private _pbLtl: boolean;
    private _shipperStopId: string;
    private _consigneeStopId: string;
    private _hasIntercompany: boolean;
    private _hasICError: boolean;
    private _hasLogTenders: boolean;
    private _hasInTenders: boolean;
    private _hasOutStatuses: boolean;
    private _hasInStatuses: boolean;

    setEdiData(responseData: ModelRow) {
        const hasEdi: boolean = responseData?.["has_edi"] ?? false;
        const hasLogistics: boolean = responseData?.["has_logistics"] ?? false;
        const hasOutStatuses: boolean = responseData?.["has_out_statuses"] ?? false;
        const hasInStatuses: boolean = responseData?.["has_in_statuses"] ?? false;
        const hasInTenders: boolean = responseData?.["has_in_tenders"] ?? false;
        const hasLogTenders: boolean = responseData?.["has_log_tenders"] ?? false;
        const inTenderCount: number = responseData?.["in_tender_count"] ?? 0;
        const outTenderCount: number = responseData?.["out_tender_count"] ?? 0;
        const inTenderError: boolean = responseData?.["in_tender_error"] ?? false;
        const outTenderError: boolean = responseData?.["out_tender_error"] ?? false;
        this.hasIntercompany = responseData?.["has_intercompany"] ?? false;
        this.hasICError = responseData?.["has_ic_error"] ?? false;
        this._pbLtl = responseData?.["pbltl_dispatched"] ?? false;

        if (!this._pbLtl && (!hasEdi && !hasLogistics || (!hasOutStatuses && !hasInStatuses && !hasLogTenders && (inTenderCount == 0 && outTenderCount == 0)))) {
            this.displayingData = false;
            return;
        }
        else if( !hasLogTenders && !hasInTenders && !hasOutStatuses && !hasInStatuses ) {
            this.displayingData = false;
            return;
        }

        this.displayingData = true;

        // Show or hide the outbound tender count
        if (hasLogTenders) {
            this.labelOutboundTenders.visible = true;
            this.labelOutboundTenderCount.visible = true;
            this.labelOutboundTenderCount.text = outTenderCount.toString();
            if (outTenderError) {
                this.labelOutboundTenderCount.color = "error";
                this.imageOutboundTenderError.visible = true;
            } else {
                this.labelOutboundTenderCount.color = "primary.dark";
                this.imageOutboundTenderError.visible = false;
            }
            this.hasLogTenders = true;
        } else {
            this.labelOutboundTenders.visible = false;
            this.labelOutboundTenderCount.visible = false;
            this.imageOutboundTenderError.visible = false;
            this.hasLogTenders = false;
        }

        // Show or hide the inbound tender count
        if (inTenderCount > 0) {
            this.labelInboundTenders.visible = true;
            this.labelInboundTenderCount.visible = true;
            this.labelInboundTenderCount.text = inTenderCount.toString();
            if (inTenderError) {
                this.labelInboundTenderCount.color = "error";
                this.imageInboundTenderError.visible = true;
            } else {
                this.labelInboundTenderCount.color = "primary.dark";
                this.imageInboundTenderError.visible = false;
            }
            this.hasInTenders = true;
        } else {
            this.labelInboundTenders.visible = false;
            this.labelInboundTenderCount.visible = false;
            this.imageInboundTenderError.visible = false;
            this.hasInTenders = false;
        }

        // Show or hide the outbound shipment status count
        if (hasOutStatuses) {
            const outboundRows: ModelRow[] = responseData?.["edi_statuses_out"];
            this.labelOutboundShipmentStatuses.visible = true;
            this.labelOutboundShipmentStatusCount.visible = true;
            if (outboundRows && outboundRows.length > 0) {
                this.labelOutboundShipmentStatusCount.text = outboundRows.length.toString();
                this.labelOutboundShipmentStatusCount.color = "primary.dark";
                this.imageOutboundShipmentStatusError.visible = false;
                const hasErrors: boolean = outboundRows.some(row =>
                    row['status_error'] > 0
                );
                this.labelOutboundShipmentStatusCount.color = hasErrors ? "error" : "primary.dark";
                this.imageOutboundShipmentStatusError.visible = hasErrors;
            } else {
                this.labelOutboundShipmentStatusCount.text = "0";
                this.labelOutboundShipmentStatusCount.color = "primary.dark";
                this.imageOutboundShipmentStatusError.visible = false;
            }
            this.hasOutStatuses = true;
        } else {
            this.labelOutboundShipmentStatuses.visible = false;
            this.labelOutboundShipmentStatusCount.visible = false;
            this.imageOutboundShipmentStatusError.visible = false;
            this.hasOutStatuses = false;
        }

        // Show or hide the inbound shipment status count
        if (hasInStatuses || this._pbLtl) {
            const inboundRows: ModelRow[] = responseData?.["edi_statuses_in"];
            this.labelInboundShipmentStatuses.visible = true;
            this.labelInboundShipmentStatusCount.visible = true;
            if (inboundRows && inboundRows.length > 0) {
                this.labelInboundShipmentStatusCount.text = inboundRows.length.toString();
                this.labelInboundShipmentStatusCount.color = "primary.dark";
                this.imageInboundShipmentStatusError.visible = false;
                const hasErrors: boolean = inboundRows.some(row =>
                    row['status_error'] > 0
                );
                this.labelInboundShipmentStatusCount.color = hasErrors ? "error" : "primary.dark";
                this.imageInboundShipmentStatusError.visible = hasErrors;
            } else {
                this.labelInboundShipmentStatusCount.text = "0";
                this.labelInboundShipmentStatusCount.color = "primary.dark";
                this.imageInboundShipmentStatusError.visible = false;
            }
            this.hasInStatuses = true;
        } else {
            this.labelInboundShipmentStatuses.visible = false;
            this.labelInboundShipmentStatusCount.visible = false;
            this.imageInboundShipmentStatusError.visible = false;
            this.hasInStatuses = false;
        }
    }

    dataFusionCompCountOnClick(event: ClickEvent) {
        const selectedCompId = event.target.id;

        if (this.movementId && this.orderId && this.customerId) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                width: window.innerWidth * .75,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;
            layout.pbLtl = this._pbLtl;
            const decorator = new SlideoutDecorator({
                layout: layout,
                title: "DataFusion - Movement " + this.movementId,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                layoutLoadListeners: (event) => {
					layout.sourceEdistatus.search({ movement_id: this.movementId });
                    layout.sourceEdiOrder.search({ order_id: this.orderId });
                    layout.sourceEdiMovementCarrier.search({ movement_id: this.movementId });
                    if(this.hasIntercompany) {
                        layout.sourceEdiMovementOrderIc.search({ order_id: this.orderId, original_order: "Y", in_movement_id: this.movementId});
                    }
                },
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.filterByMovement = true;
                    layout.saveButton.addClickListener(event => {
                        if (decorator.validateSimple()) {
                            const dataLayout = layout.outboundDataSource.data;
                            dataLayout?.forEach(async (data, index) => {
                                if (data.hasChanged()) {
                                    await layout.outboundDataSource.data[index].post();
                                }
                            });
                        }
                    });

                    let selIndex: number = 0;
                    if (selectedCompId == this.imageIntercompany.id || selectedCompId == this.imageIntercompanyError.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabIntercompany);
                    } else if (selectedCompId == this.labelInboundTenderCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabInboundTender);
                    } else if (selectedCompId == this.labelOutboundTenderCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabOutboundTender);
                    } else if (selectedCompId == this.labelOutboundShipmentStatusCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    } else if (selectedCompId == this.labelInboundShipmentStatusCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabInbound);
                    }

                    layout.tabsetDataFusion.selectedIndex = selIndex;
                },
                onClose: () => {
                    decorator.slideOut();
                },
                doAfterSlideOut: () => {
                    const errorsCallbackFunction = (row: TableRow) => { return row.data.get("error_count") === "0 Errors" };
                    if (selectedCompId == this.labelInboundShipmentStatusCount.id) {
                        const currentCount: number = parseInt(this.labelInboundShipmentStatusCount.text);
                        const layoutCount: number = layout.tableInbound.rows.length;
                        if (currentCount !== layoutCount) {
                            this.labelInboundShipmentStatusCount.text = layoutCount.toString();
                        }
                        const currentError: boolean = this.imageInboundShipmentStatusError.visible;
                        const layoutError: boolean = !layout.tableInbound.rows.every(errorsCallbackFunction);
                        if (currentError !== layoutError) {
                            this.imageInboundShipmentStatusError.visible = layoutError;
                            this.labelInboundShipmentStatusCount.color = layoutError ? "error" : "primary.dark";
                        }
                    } else if (selectedCompId == this.labelOutboundShipmentStatusCount.id) {
                        const currentCount: number = parseInt(this.labelOutboundShipmentStatusCount.text);
                        const layoutCount: number = layout.tableOutbound.rows.length;
                        if (currentCount !== layoutCount) {
                            this.labelOutboundShipmentStatusCount.text = layoutCount.toString();
                        }
                        const currentError: boolean = this.imageOutboundShipmentStatusError.visible;
                        const layoutError: boolean = !layout.tableOutbound.rows.every(errorsCallbackFunction);
                        if (currentError !== layoutError) {
                            this.imageOutboundShipmentStatusError.visible = layoutError;
                            this.labelOutboundShipmentStatusCount.color = layoutError ? "error" : "primary.dark";
                        }
                    }
                }
            });

            layout.orderId = this.orderId;
            layout.movementId = this.movementId;
            layout.customerId = this.customerId;
        }
    }

    get movementId(): string {
        return this._movementId;
    }

    set movementId(movementId: string) {
        this._movementId = movementId;
    }

    get orderId(): string {
        return this._orderId;
    }

    set orderId(orderId: string) {
        this._orderId = orderId;
    }

    get customerId(): string {
        return this._customerId;
    }

    set customerId(customerId: string) {
        this._customerId = customerId;
    }

    get displayingData(): boolean {
        return this._displayingData;
    }

    set displayingData(displayingData: boolean) {
        this._displayingData = displayingData;
    }

    public get shipperStopId(): string {
        return this._shipperStopId;
    }

    public set shipperStopId(value: string) {
        this._shipperStopId = value;
    }

    public get consigneeStopId(): string {
        return this._consigneeStopId;
    }

    public set consigneeStopId(value: string) {
        this._consigneeStopId = value;
    }

    public get hasInTenders(): boolean {
        return this._hasInTenders;
    }

    public set hasInTenders(value: boolean) {
        this._hasInTenders = value;
    }

    public get hasLogTenders(): boolean {
        return this._hasLogTenders;
    }

    public set hasLogTenders(value: boolean) {
        this._hasLogTenders = value;
    }

    public get hasInStatuses(): boolean {
        return this._hasInStatuses;
    }

    public set hasInStatuses(value: boolean) {
        this._hasInStatuses = value;
    }

    public get hasOutStatuses(): boolean {
        return this._hasOutStatuses;
    }

    public set hasOutStatuses(value: boolean) {
        this._hasOutStatuses = value;
    }

    public get hasIntercompany(): boolean {
        return this._hasIntercompany;
    }

    public set hasIntercompany(value: boolean) {
        this._hasIntercompany = value;
    }

    public get hasICError(): boolean {
        return this._hasICError;
    }

    public set hasICError(value: boolean) {
        if(this.hasIntercompany) {
            this._hasICError = value;
            this.imageIntercompanyError.color = "primary";
            this.imageIntercompanyError.visible = value;
            this.imageIntercompany.visible = !value;
        } else {
            this.imageIntercompanyError.visible = false;
            this.imageIntercompany.visible = false;
        }

    }
}
